import { DataSource } from "../../databinding/DataSource";
import { ComponentTypes } from "../../base/ComponentTypes";
import { SaveButtonPropDefinitions, SaveButtonProps } from "./SaveButtonProps";
import { Label } from "../label/Label";
import { MultiButton, StringOrPropsOrComponent } from "../..";
import { ArrayUtil, getThemeColor, ModelRow, ObjectUtil } from "@mcleod/core";
import { S } from "chart.js/dist/chunks/helpers.core";

export enum SaveAction {
  SAVE_AND_ADD = "saveAndAdd",
  SAVE_AND_CLOSE = "saveAndClose",
  SAVE = "save"
}
/**
 * This class is a button that will post its associated DataSource.
 */
export class SaveButton extends MultiButton {
  private _allowSave: boolean;
  private _allowClose: boolean;
  private _allowAddNew: boolean;
  public onClose: () => void;
  public onSave: (row: ModelRow<any>) => void;
  private labelSave: Label;
  private labelSaveAdd: Label;
  private _otherLabels: Label[]=[];
  private _extraDataSources: DataSource[];
  private designerEnabledValue: boolean;
  private _enabledMap = new Map<DataSource, boolean>();
  private _saveAction: SaveAction;

  constructor(props?: Partial<SaveButtonProps>) {
    super({ id: props?.id }); // don't pass the full props because it could require labelSave/labelSaveAdd to have been initialized
    this.createDropdownItems();
    this.primaryButton.caption = SaveButtonPropDefinitions.getDefinitions().caption.defaultValue,
    this.allowSave = true;
    this.busyWhenDataSourceBusy = true;
    this.setProps({
      color: SaveButtonPropDefinitions.getDefinitions().color.defaultValue,
      backgroundColor: SaveButtonPropDefinitions.getDefinitions().backgroundColor.defaultValue,
      minWidth: SaveButtonPropDefinitions.getDefinitions().minWidth.defaultValue,
      ...props
    });
    this.primaryButton.addClickListener(_event => {
      if (this.allowClose)
        this.saveAndClose();
      else if (this.allowSave)
        this.save();
      else if (this.allowAddNew)
        this.saveAndAdd();
    });
  }

  get allowSave(): boolean {
    return this._allowSave;
  }

  set allowSave(value: boolean) {
    this._allowSave = value;
    this.rebuildDropdownItems();
  }

  get allowClose(): boolean {
    return this._allowClose;
  }

  set allowClose(value: boolean) {
    this._allowClose = value;
    this.rebuildDropdownItems();
  }

  get allowAddNew(): boolean {
    return this._allowAddNew;
  }

  set allowAddNew(value: boolean) {
    this._allowAddNew = value;
    this.rebuildDropdownItems();
  }

  get busy(): boolean {
    return super.busy;
  }

  set busy(value: boolean) {
    if (this.allowClose === true) {
      if (value !== true) {
        this.primaryButton.paddingTop = 10;
        this.primaryButton.paddingRight = 7;
        this.primaryButton.paddingBottom = 10;
        this.primaryButton.paddingLeft = 14;
        this.primaryButton.imageProps = { ...this.primaryButton.imageProps, marginRight: 4, marginLeft: 0 };
      }
      else {
        this.primaryButton.paddingTop = 4;
        this.primaryButton.paddingRight = 5;
        this.primaryButton.paddingBottom = 2;
        this.primaryButton.paddingLeft = 6;
        this.primaryButton.imageProps = { ...this.primaryButton.imageProps, marginRight: 0, marginLeft: 4 };
      }
    }
    if (this.primaryButton != null)
      super.busy = value;
  }

  private rebuildDropdownItems() {
    const existing: StringOrPropsOrComponent[] = this.dropdownItems;
    const replaced = [];
    if (this.allowClose) {
      this.primaryButton.setProps({ caption: "Save & Close" });
      if (this.allowSave)
        replaced.push(this.labelSave);
      if (this.allowAddNew)
        replaced.push(this.labelSaveAdd);
    }
    else if (this.allowSave) {
      this.primaryButton.setProps({ caption: "Save" });
      if (this.allowAddNew)
        replaced.push(this.labelSaveAdd);
    }
    else if (this.allowAddNew)
      this.primaryButton.setProps({ caption: "Save & Add" });

    if (existing != null)
      for (const item of existing)
        if (item != this.labelSave && this.labelSaveAdd)
          replaced.push(item)
    if( this._otherLabels != null && this._otherLabels.length > 0){
      this._otherLabels.forEach( label =>{
        replaced.push(label);
      });
    }
    if (!ObjectUtil.deepEqual(existing, replaced))
      this.dropdownItems = replaced;
  }

  get isCloseAvailable(): boolean {
    return this.allowClose; // make sure that we have something to close
  }

  private createDropdownItems() {
    this.labelSave = new Label({ caption: "Save", onClick: () => this.save() });
    this.labelSaveAdd = new Label({ caption: "Save & Add", onClick: () => this.saveAndAdd() });
  }

  private save() {
    this.saveAction = SaveAction.SAVE;
    this.performPost();
  }

  private saveAndAdd() {
    this.saveAction = SaveAction.SAVE_AND_ADD;
    this.performPost();
  }

  private saveAndClose() {
    this.saveAction = SaveAction.SAVE_AND_CLOSE;
    this.performPost().then(row => {
      if (this.onClose != null)
        this.onClose();
    });
  }

  performPost(): Promise<ModelRow> {
    if (this.dataSource != null) {
      return this.dataSource.post().then(async postedRow => {
        if (this.onSave != null)
          this.onSave(postedRow);
        if (this.extraDataSources != null)
          for (const source of this.extraDataSources)
            await source.post();
        return postedRow;
      });
    }
  }

  public get extraDataSources(): DataSource[] {
    return this._extraDataSources;
  }

  /**
   * The extraDataSources prop allows the SaevButton to be tied to multiple DataSources.
   * This means that we can create a page with data that comes from different models and
   * post it all in one click (without the need for DataSource listeners).
   * Currently this prop isn't available in the UI Designer and must be set from code
   * (typically the onLoad() method).  At some point (with some UI Designer work), it's planned that
   * this prop is settable in the UI Designer.
   * Any change made to any of its DataSources will cause the SaveButton to be enabled.
   * Clicking the SaveButton will post the main dataSource and all its extraDataSources.
   */
  public set extraDataSources(value: DataSource[]) {
    if (this._extraDataSources != null) {
      for (const source of this._extraDataSources) {
        source.removeHasChangedComponent(this);
        this._enabledMap.delete(source);
      }
    }
    this._extraDataSources = value;
    if (this._extraDataSources != null) {
      for (const source of value)
        source.addHasChangedComponent(this);
    }
    this._setEnabledFromDataSources();
  }

  /*
  public addExtraDataSource(value: DataSource) {
    const tempExtra = [];

    if (this._extraDataSources != null) {
      for (const source of this._extraDataSources) {
        tempExtra.push(source);
      }
    }
    tempExtra.push(value);
    this.extraDataSources = tempExtra;
  }
  */

  private _setEnabledFromDataSources() {
    const allDataSources = [];
    if (this.dataSource != null)
      allDataSources.push(this.dataSource);
    if (ArrayUtil.isEmptyArray(this._extraDataSources) !== true)
      allDataSources.push(...this._extraDataSources);

    this._enabledMap.clear();
    let enabled = ArrayUtil.isEmptyArray(allDataSources) !== true;
    for (const source of allDataSources) {
      const dsChanged = source.hasChanged();
      this._enabledMap.set(source, dsChanged);
      enabled = enabled && dsChanged;
    }
    this.internalSetEnabled(enabled);
    this._setDisabledTooltip(this.enabled);
  }

  /**
   * Override get/set enabled methods to allow us to keep track of an enabled value that is valid in the designer.
   * We don't want the SaveButton to become disabled in the UI designer (which it can), and then have that enabled
   * value be serialized into the layout.  Instead, the designerEnabledValue, which should be the value of enabled
   * without any automated setting of the value based on DataSources, should be the value that is serialized.
   */
  public override get enabled(): boolean {
    if (this.__designer != null)
      return this.designerEnabledValue;
    return super.enabled;
  }

  public override set enabled(value: boolean) {
    this.internalSetEnabled(value);
    this.designerEnabledValue = value;
  }

  private internalSetEnabled(value: boolean) {
    super.enabled = value;
  }

  public get dataSource(): DataSource {
    return super.dataSource;
  }

  public set dataSource(value: DataSource) {
    if (this.dataSource === value)
      return;
    this.dataSource?.removeHasChangedComponent(this);
    this._enabledMap.delete(this.dataSource);
    super.dataSource = value;
    this.dataSource?.addHasChangedComponent(this);
    this._setEnabledFromDataSources();
  }

  public removeFromHasChangedComponents() {
    this.dataSource?.removeHasChangedComponent(this);
    if (this._extraDataSources != null) {
      for (const source of this._extraDataSources) {
        source.removeHasChangedComponent(this);
        this._enabledMap.delete(source);
      }
    }
  }

  private _setDisabledTooltip(value: boolean) {
    if (value === false)
      this.disabledTooltip = "You cannot save this record because nothing has changed.";
    else
      this.disabledTooltip = null;
  }

  doOnDataSourceChanged(dataSource: DataSource, value: boolean) {
    this._enabledMap.set(dataSource, value);
    let enabled = this._enabledMap.size !== 0;
    for (const value of this._enabledMap.values()) {
      enabled = enabled && value;
    }
    this.internalSetEnabled(enabled);
    this._setDisabledTooltip(this.enabled);
  }

  override getPropertyDefinitions() {
    return SaveButtonPropDefinitions.getDefinitions();
  }

  override get serializationName() {
    return "savebutton";
  }
  override _applyEnabled(value: boolean): void {
    if (value) {
      this._element.removeAttribute("disabled");
      this.color = this.color;
      this.backgroundColor = this.backgroundColor;
      this.cursor = this.cursor;
    }
    else {
      this._element.setAttribute("disabled", "true");
      this._element.style.backgroundColor = getThemeColor("primary.lightest");
      this._element.style.color = getThemeColor("subtle.light");
      this._element.style.cursor = "";
    }
  }

  addDropdownItems(labels: Label[]) {
    this._otherLabels=labels;
    this.rebuildDropdownItems();
  }

  get otherLabels(){
    return this._otherLabels;
  }

  set saveAction(value: SaveAction) {
    this._saveAction = value;
  }
  get saveAction(): SaveAction {
    return this._saveAction;
   }
}

ComponentTypes.registerComponentType("savebutton", SaveButton.prototype.constructor, false);
